import axios from "axios";
import { API_URL } from "../constant";

const API_BASE_URL = process.env.REACT_APP_API_URL || API_URL;

// Create axios instance with auth headers
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const advertisingAgentAPI = {
  getAgentRetailBusinesses: async (userId) => {
    const response = await api.get(`/superadmin/customer-agents/${encodeURIComponent(userId)}/retail-businesses`);
    return response.data;
  },
  // Get all advertising agents (Super Admin only)
  getAllAgents: async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.per_page) params.append("per_page", filters.per_page);
    if (filters.page) params.append("page", filters.page);
    if (filters.status && filters.status !== "all")
      params.append("status", filters.status);
    if (filters.agent_type && filters.agent_type !== "all")
      params.append("agent_type", filters.agent_type);
    if (filters.search) params.append("search", filters.search);

    const response = await api.get(`/superadmin/advertising-agents?${params}`);
    return response.data;
  },

  // Get agent applications
  getAgentApplications: async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.per_page) params.append("per_page", filters.per_page);
    if (filters.page) params.append("page", filters.page);
    if (filters.status && filters.status !== "all")
      params.append("status", filters.status);
    if (filters.agent_type && filters.agent_type !== "all")
      params.append("agent_type", filters.agent_type);
    if (filters.search)
      params.append("search", filters.search);

    const response = await api.get(
      `/superadmin/advertising-agents-applications?${params}`,
    );
    return response.data;
  },

  // Get specific agent application
  getAgentApplicationById: async (applicationId) => {
    const response = await api.get(
      `/superadmin/advertising-agents-applications/${applicationId}`,
    );
    return response.data;
  },

  reviewAgentApplication: async (
    applicationId,
    action,
    rejectionReason = "",
    commissionRate = null,
    agentType = null,
  ) => {
    const data = {
      action,
      rejection_reason: rejectionReason,
      ...(commissionRate !== null && { commission_rate: commissionRate }),
      ...(agentType && { agent_type: agentType }),
    };

    if (action === "rejected") {
      data.rejection_reason = rejectionReason;
    }
    return api.post(
      `/superadmin/advertising-agents-applications/review/${applicationId}`,
      data,
    );
  },

  // Edit agent application (for pending status)
  editAgentApplication: async (applicationId, agentType, notes = "") => {
    const data = {
      agent_type: agentType,
      notes,
    };

    return api.put(
      `/superadmin/agent-applications/${applicationId}/edit`,
      data,
    );
  },

  // Directly create agent from customer (without application)
  createDirectAdvertisingAgent: async (
    userId,
    agentType,
    commissionRate,
    isActive = true,
    notes = "",
  ) => {
    const data = {
      agent_type: agentType,
      commission_rate: commissionRate,
      is_active: isActive,
      notes,
    };

    return api.post(`/superadmin/customers/${userId}/create-agent`, data);
  },

  // Update existing agent type (mini ↔ senior)
  updateAgentType: async (agentId, agentType, commissionRate, reason = "") => {
    const data = {
      agent_type: agentType,
      commission_rate: commissionRate,
      reason,
    };

    return api.put(`/superadmin/agents/${agentId}/update-type`, data);
  },

  // Directly create agent from customer (without application)
  createDirectAdvertisingAgent: async (
    userId,
    agentType,
    commissionRate,
    isActive = true,
    notes = "",
  ) => {
    const data = {
      agent_type: agentType,
      commission_rate: commissionRate,
      is_active: isActive,
      notes,
    };

    return api.post(`/superadmin/customers/${userId}/create-agent`, data);
  },

  // Update existing agent type (mini ↔ senior)
  updateAgentType: async (agentId, agentType, commissionRate, reason = "") => {
    const data = {
      agent_type: agentType,
      commission_rate: commissionRate,
      reason,
    };

    return api.put(`/superadmin/agents/${agentId}/update-type`, data);
  },

  // Get application by ID
  getAgentApplicationById: async (applicationId) => {
    return api.get(`/superadmin/agent-applications/${applicationId}`);
  },

  // Get agent details
  getAgentDetails: async (agentId) => {
    const response = await api.get(`/superadmin/advertising-agents/${agentId}`);
    return response.data;
  },

  // Update agent status
  updateAgentStatus: async (agentId, isActive, commissionRate = null) => {
    const data = { is_active: isActive };

    if (commissionRate !== null) {
      data.commission_rate = commissionRate;
    }

    const response = await api.put(
      `/superadmin/advertising-agents/${agentId}/status`,
      data,
    );
    return response.data;
  },

  // Demote mini agent to customer
  demoteToCustomer: async (agentId, reason) => {
    const response = await api.post(
      `/superadmin/advertising-agents/${agentId}/demote-to-customer`,
      { reason }
    );
    return response.data;
  },

  // Upgrade agent to senior
  upgradeToSeniorAgent: async (agentId, commissionRate = 15.0) => {
    const response = await api.post(
      `/superadmin/advertising-agents/upgrade/${agentId}`,
      {
        commission_rate: commissionRate,
      },
    );
    return response.data;
  },

  // Get mini agents
  getMiniAgents: async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.per_page) params.append("per_page", filters.per_page);
    if (filters.page) params.append("page", filters.page);
    if (filters.status && filters.status !== "all")
      params.append("status", filters.status);
    if (filters.search) params.append("search", filters.search);

    const response = await api.get(
      `/superadmin/mini-advertising-agents?${params}`,
    );
    return response.data;
  },

  // Get senior agents
  getSeniorAgents: async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.per_page) params.append("per_page", filters.per_page);
    if (filters.page) params.append("page", filters.page);
    if (filters.status && filters.status !== "all")
      params.append("status", filters.status);
    if (filters.search) params.append("search", filters.search);

    const response = await api.get(
      `/superadmin/senior-advertising-agents?${params}`,
    );
    return response.data;
  },

  // Get agent stats
  getAgentStats: async () => {
    const response = await api.get("/superadmin/advertising-agents/stats");
    return response.data;
  },

  // Get agent referral history
  getAgentReferralHistory: async (agentId, filters = {}) => {
    const params = new URLSearchParams();

    if (filters.per_page) params.append("per_page", filters.per_page);
    if (filters.page) params.append("page", filters.page);

    const response = await api.get(
      `/superadmin/advertising-agents/${agentId}/referral-history?${params}`,
    );
    return response.data;
  },

  // Get agent's own referral history (for agent dashboard)
  getMyAgentReferralHistory: async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.per_page) params.append("per_page", filters.per_page);
    if (filters.page) params.append("page", filters.page);

    const response = await api.get(`/agent/referral-history?${params}`);
    return response.data;
  },

  getAgentReferralHistory: async (agentId, filters = {}) => {
    const params = new URLSearchParams();

    if (filters.per_page) params.append("per_page", filters.per_page);
    if (filters.page) params.append("page", filters.page);

    const response = await api.get(
      `/superadmin/advertising-agents/referral-history/${agentId}?${params}`,
    );
    return response.data;
  },

  // Get agent's own referral history (for agent dashboard)
  getMyAgentReferralHistory: async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.per_page) params.append("per_page", filters.per_page);
    if (filters.page) params.append("page", filters.page);

    const response = await api.get(`/agent/referral-history?${params}`);
    return response.data;
  },

  // Get agent transaction history (commissions)
  getAgentTransactionHistory: async (agentId, filters = {}) => {
    const params = new URLSearchParams();

    if (filters.per_page) params.append("per_page", filters.per_page);
    if (filters.page) params.append("page", filters.page);

    const response = await api.get(
      `/superadmin/advertising-agents/${agentId}/transaction-history?${params}`,
    );
    return response.data;
  },

  // Get agent's own transaction history
  getMyAgentTransactionHistory: async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.per_page) params.append("per_page", filters.per_page);
    if (filters.page) params.append("page", filters.page);

    const response = await api.get(`/agent/transaction-history?${params}`);
    return response.data;
  },

  // Get detailed referral information with QR code
  getAgentReferralDetails: async () => {
    const response = await api.get("/agent/referral-details");
    return response.data;
  },

  // Generate QR code
  generateReferralQRCode: async () => {
    const response = await api.get("/agent/generate-referral-qr");
    return response.data;
  },

  // Download QR code
  downloadReferralQRCode: async () => {
    const response = await api.get("/agent/download-referral-qr", {
      responseType: "blob",
    });
    return response;
  },

  getAdvertisingAgentCount: async (filters = {}) => {
    try {
      const response = await api.get("/superadmin/count/advertising-agents", {
        params: filters,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching advertising agent count:", error);
      throw error;
    }
  },

  // Get all available admin areas with filters
  getAvailableAdminAreas: async (params = {}) => {
    try {
      const response = await api.get("/superadmin/admin-area/available", {
        params,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching admin areas:", error);
      throw error.response?.data || error;
    }
  },

  // Get counties for filtering
  getCounties: async () => {
    try {
      const response = await api.get("/superadmin/admin-areas/counties");
      return response.data;
    } catch (error) {
      console.error("Error fetching counties:", error);
      throw error.response?.data || error;
    }
  },

  // Get customer's assigned admin areas
  getCustomerAdminAreas: async (customerId) => {
    try {
      const response = await api.get(
        `/superadmin/admin-areas/agent/${customerId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching customer admin areas:", error);
      throw error.response?.data || error;
    }
  },

  // Assign admin areas to customer
  assignAdminAreas: async (
    customerId,
    adminAreaIds,
    replaceExisting = false,
  ) => {
    try {
      const response = await api.post(
        `/superadmin/admin-areas/assign/${customerId}`,
        {
          admin_area_ids: adminAreaIds,
          replace_existing: replaceExisting,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error assigning admin areas:", error);
      throw error.response?.data || error;
    }
  },

  // Remove admin areas from customer
  removeAdminAreas: async (customerId, adminAreaIds) => {
    try {
      const response = await api.delete(
        `/superadmin/admin-areas/agent/${customerId}/remove`,
        {
          data: { admin_area_ids: adminAreaIds },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error removing admin areas:", error);
      throw error.response?.data || error;
    }
  },

  // Get customers by admin area
  getCustomersByAdminArea: async (adminAreaId) => {
    try {
      const response = await api.get(
        `/superadmin/admin-areas/area/${adminAreaId}/agents`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching customers by admin area:", error);
      throw error.response?.data || error;
    }
  },

  getAgentAdminAreas: async (agentId) => {
    try {
      const response = await api.get(
        `/superadmin/admin-areas/agent/${agentId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching agent admin areas:", error);
      throw error.response?.data || error;
    }
  },

  // Assign focus countries to an advertising agent
  assignFocusCountries: async (
    agentId,
    focusCountriesData,
    replaceExisting = false,
  ) => {
    try {
      const response = await api.post(
        `/superadmin/advertising-agents/${agentId}/focus-countries`,
        {
          focus_countries: focusCountriesData,
          replace_existing: replaceExisting,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error assigning focus countries:", error);
      throw error.response?.data || error;
    }
  },

  // Get focus countries for a specific advertising agent
  getAgentFocusCountries: async (agentId) => {
    try {
      const response = await api.get(
        `/superadmin/advertising-agents/${agentId}/focus-countries`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching agent focus countries:", error);
      throw error.response?.data || error;
    }
  },

  // Remove specific focus countries from an advertising agent
  removeFocusCountries: async (agentId, countryIds) => {
    try {
      const response = await api.delete(
        `/superadmin/advertising-agents/${agentId}/focus-countries`,
        {
          data: { country_ids: countryIds },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error removing focus countries:", error);
      throw error.response?.data || error;
    }
  },

  // Update focus country type or notes
  updateFocusCountry: async (agentId, countryId, updateData) => {
    try {
      const response = await api.put(
        `/superadmin/advertising-agents/${agentId}/focus-countries/${countryId}`,
        updateData,
      );
      return response.data;
    } catch (error) {
      console.error("Error updating focus country:", error);
      throw error.response?.data || error;
    }
  },

  // Get available countries for focus (excluding already assigned ones for the agent)
  getAvailableFocusCountries: async (agentId, filters = {}) => {
    try {
      const params = new URLSearchParams();

      if (filters.region) params.append("region", filters.region);
      if (filters.search) params.append("search", filters.search);

      const response = await api.get(
        `/superadmin/advertising-agents/${agentId}/available-focus-countries?${params}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching available focus countries:", error);
      throw error.response?.data || error;
    }
  },

  // Get all agents with their focus countries (Super Admin only)
  getAllAgentsWithFocusCountries: async (filters = {}) => {
    try {
      const params = new URLSearchParams();

      if (filters.agent_type && filters.agent_type !== "all")
        params.append("agent_type", filters.agent_type);
      if (filters.focus_type) params.append("focus_type", filters.focus_type);
      if (filters.country_id) params.append("country_id", filters.country_id);
      if (filters.per_page) params.append("per_page", filters.per_page);

      const response = await api.get(
        `/superadmin/advertising-agents/focus-countries/all?${params}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching all agents with focus countries:", error);
      throw error.response?.data || error;
    }
  },

  // Get focus countries statistics (Super Admin only)
  getFocusCountriesStatistics: async () => {
    try {
      const response = await api.get(
        `/superadmin/advertising-agents/focus-countries/statistics`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching focus countries statistics:", error);
      throw error.response?.data || error;
    }
  },

  // Public endpoint to get agents by focus country (for public display)
  getPublicAgentsByFocusCountry: async (countryId, filters = {}) => {
    try {
      const params = new URLSearchParams();

      if (filters.agent_type && filters.agent_type !== "all")
        params.append("agent_type", filters.agent_type);
      if (filters.focus_type && filters.focus_type !== "all")
        params.append("focus_type", filters.focus_type);
      if (filters.per_page) params.append("per_page", filters.per_page);

      const response = await api.get(
        `/public/advertising-agents/focus-country/${countryId}?${params}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching public agents by focus country:", error);
      throw error.response?.data || error;
    }
  },

  // Public endpoint to get focus countries for a specific agent
  getPublicAgentFocusCountries: async (agentId) => {
    try {
      const response = await api.get(
        `/public/advertising-agents/${agentId}/focus-countries`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching public agent focus countries:", error);
      throw error.response?.data || error;
    }
  },
};

export default api;
